import { useMemo } from 'react'
import Big from 'big.js'
import { useAtomValue } from 'jotai'

import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import {
  DISPLAYED_DECIMAL_PLACES,
  toAppliedGrantCredit,
  toTotalAfterCreditGrants
} from '@sequencehq/invoice-content'
import { toMoney } from '@sequencehq/utils'
import {
  invoiceAppliedCreditGrantsAtom,
  invoiceAppliedCreditGrantTaxAtom,
  invoiceCurrencyAtom,
  invoiceGrossTotalAtom,
  invoiceProjectedCreditGrantsAtom,
  invoiceStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

const PROJECTABLE_INVOICE_STATUSES: ReadonlyArray<Dashboardv99990101Api.Invoices.InvoiceStatus> =
  ['DRAFT', 'IN_PROGRESS']

/**
 * Credit the invoice is expected to receive when it is finalised.
 *
 * A burndown pot names no grant, so the backend sends one unattributed row.
 * Itemising it is IE-3393.
 */
export const useProjectedCreditGrantTotal = (): Big => {
  const projectedCreditGrants = useAtomValue(invoiceProjectedCreditGrantsAtom)
  const grossTotal = useAtomValue(invoiceGrossTotalAtom) ?? '0'
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const { useCreditGrantLedger } = useFeatureFlags()

  const canProjectCredit =
    useCreditGrantLedger &&
    !!invoiceStatus &&
    PROJECTABLE_INVOICE_STATUSES.includes(invoiceStatus)

  return useMemo(() => {
    const projected = (canProjectCredit ? projectedCreditGrants : [])
      .reduce((total, row) => total.plus(row.amount), new Big(0))
      .round(DISPLAYED_DECIMAL_PLACES)

    // The backend caps against the total *before* V1 cash credit, which sits
    // above gross once the draft carries cash-credit lines, so the figure can
    // exceed what the invoice owes. Capping keeps the row, "Total due" and the
    // header agreeing; the base itself is a backend fix.
    const gross = new Big(grossTotal)
    const cap = gross.gt(0) ? gross : new Big(0)

    return projected.gt(cap) ? cap : projected
  }, [canProjectCredit, projectedCreditGrants, grossTotal])
}

export const useHumanReadableTotalAfterCreditGrants = (): string => {
  const currency = useAtomValue(invoiceCurrencyAtom) ?? 'USD'
  const grossTotal = useAtomValue(invoiceGrossTotalAtom) ?? '0'
  const appliedCreditGrants = useAtomValue(invoiceAppliedCreditGrantsAtom)
  const appliedCreditGrantTax = useAtomValue(invoiceAppliedCreditGrantTaxAtom)
  const { useCreditGrantLedger } = useFeatureFlags()
  const projectedCredit = useProjectedCreditGrantTotal()

  const creditApplied = useMemo(
    () =>
      toAppliedGrantCredit({
        appliedCreditGrants: useCreditGrantLedger ? appliedCreditGrants : [],
        appliedCreditGrantTax: useCreditGrantLedger
          ? appliedCreditGrantTax
          : undefined
      }),
    [useCreditGrantLedger, appliedCreditGrants, appliedCreditGrantTax]
  )

  const totalAfterCredit = toTotalAfterCreditGrants({
    grossTotal,
    creditApplied,
    creditProjected: projectedCredit.toString()
  })

  // Preserve the headline's zero floor even when no grant credit applies.
  const displayed = new Big(totalAfterCredit).lt(0) ? '0' : totalAfterCredit

  return toMoney({ currency, value: displayed })
}
